import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  type MeshStandardMaterial,
} from "three";

import type { StreetSurfaceFamily } from "../../config/streets.ts";
import {
  createStreetSurfaceMeshSet,
  PEDESTRIAN_SURFACE_CLASSES,
  ROAD_SURFACE_CLASSES,
  type PedestrianSurfaceClass,
  type RoadSurfaceClass,
  type StreetMeshData,
} from "../../world/streets.ts";
import type { RuntimeLinearFeature } from "../../world/schema/runtimeTile.ts";
import {
  parseWorldTileId,
  type TerrainTileData,
  WORLD_TILE_SIZE_M,
  type WorldTileId,
} from "../../world/schema/tile.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface StreetSurfaceBatch {
  readonly object: Group;
  readonly carriagewayCount: number;
  readonly pedestrianCount: number;
}

export interface ReducedStreetSurfaceBatch extends StreetSurfaceBatch {
  readonly junctionCount: number;
}

export function createStreetSurfaceBatch(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  materials: CityMaterialLibrary,
): StreetSurfaceBatch | null {
  const meshSet = createStreetSurfaceMeshSet(features, terrain, family, {
    tileOrigin: tileOriginForId(tileId),
  });
  const materialSet = materials.streetSurfaces[family];
  const object = new Group();
  object.name = `runtime-street-surfaces:${tileId}`;
  object.userData = {
    tileId,
    sourceLayer: "transport-centerlines",
    styleFamily: family,
    representation:
      "reference-derived-terrain-following-carriageway-curb-sidewalk",
  };
  for (const surfaceClass of ROAD_SURFACE_CLASSES) {
    addMesh(
      object,
      meshSet.carriagewaySurfaces[surfaceClass],
      roadSurfaceMaterial(materials, family, surfaceClass, false),
      `${object.name}:carriageway:${surfaceClass}`,
      {
        featureIds: meshSet.carriagewayFeatureIds,
        surface: roadSurfaceLabel(family, surfaceClass),
        sourceSurfaceClass: surfaceClass,
      },
    );
  }
  addMesh(
    object,
    meshSet.shoulders,
    materialSet.carriageway,
    `${object.name}:shoulders`,
    {
      featureIds: meshSet.carriagewayFeatureIds,
      surface: "official-road-platform-outside-carriageway",
    },
  );
  addMesh(
    object,
    meshSet.elevatedDecks,
    materialSet.curb,
    `${object.name}:elevated-decks`,
    {
      featureIds: meshSet.carriagewayFeatureIds,
      surface: "source-verified-grade-separated-road-deck",
    },
  );
  for (const surfaceClass of ROAD_SURFACE_CLASSES) {
    addMesh(
      object,
      meshSet.junctionSurfaces[surfaceClass],
      roadSurfaceMaterial(materials, family, surfaceClass, true),
      `${object.name}:junctions:${surfaceClass}`,
      {
        featureIds: meshSet.carriagewayFeatureIds,
        junctionCount: meshSet.junctionCount,
        surface: `continuous-${roadSurfaceLabel(family, surfaceClass)}-junction`,
        sourceSurfaceClass: surfaceClass,
      },
    );
  }
  addMesh(
    object,
    meshSet.sidewalks,
    materialSet.sidewalk,
    `${object.name}:sidewalks`,
    {
      featureIds: meshSet.carriagewayFeatureIds,
      surface: family === "old_town"
        ? "dressed-stone-pavers"
        : "rectangular-concrete-slabs",
    },
  );
  addMesh(
    object,
    meshSet.curbs,
    materialSet.curb,
    `${object.name}:curbs`,
    {
      featureIds: meshSet.carriagewayFeatureIds,
      surface: family === "old_town"
        ? "low-stone-edge"
        : "raised-precast-curb",
    },
  );
  for (const surfaceClass of PEDESTRIAN_SURFACE_CLASSES) {
    addMesh(
      object,
      meshSet.pedestrianSurfaces[surfaceClass],
      pedestrianSurfaceMaterial(materials, family, surfaceClass),
      `${object.name}:pedestrian:${surfaceClass}`,
      {
        featureIds: meshSet.pedestrianFeatureIds,
        surface: pedestrianSurfaceLabel(family, surfaceClass),
        sourceSurfaceClass: surfaceClass,
      },
    );
  }
  if (object.children.length === 0) {
    return null;
  }
  return {
    object,
    carriagewayCount: meshSet.carriagewayFeatureIds.length,
    pedestrianCount: meshSet.pedestrianFeatureIds.length,
  };
}

function roadSurfaceMaterial(
  materials: CityMaterialLibrary,
  family: StreetSurfaceFamily,
  surfaceClass: RoadSurfaceClass,
  junction: boolean,
): MeshStandardMaterial {
  if (surfaceClass === "default") {
    return junction
      ? materials.streetSurfaces[family].junction
      : materials.streetSurfaces[family].carriageway;
  }
  return materials.officialRoadSurfaces[surfaceClass];
}

function roadSurfaceLabel(
  family: StreetSurfaceFamily,
  surfaceClass: RoadSurfaceClass,
): string {
  if (surfaceClass !== "default") {
    return `official-grpk-${surfaceClass.replaceAll("_", "-")}`;
  }
  return family === "old_town" ? "district-fallback-fan-cobble" : "district-fallback-asphalt";
}

function pedestrianSurfaceMaterial(
  materials: CityMaterialLibrary,
  family: StreetSurfaceFamily,
  surfaceClass: PedestrianSurfaceClass,
): MeshStandardMaterial {
  if (surfaceClass === "default") {
    return materials.streetSurfaces[family].pedestrian;
  }
  return materials.mappedPedestrianSurfaces[surfaceClass];
}

function pedestrianSurfaceLabel(
  family: StreetSurfaceFamily,
  surfaceClass: PedestrianSurfaceClass,
): string {
  if (surfaceClass !== "default") {
    return surfaceClass === "cycleway"
      ? "mapped-osm-designated-cycleway"
      : `mapped-osm-pedestrian-${surfaceClass}`;
  }
  return family === "old_town"
    ? "district-fallback-pedestrian-pavers"
    : "district-fallback-concrete-pedestrian";
}

/**
 * Preserves the authored road widths, continuous junctions, sidewalks, and
 * pedestrian paths in reduced world tiles while keeping the distant budget at
 * two textureless draw calls per tile. Curbs and close-range paving stay in
 * LOD0.
 */
export function createReducedStreetSurfaceBatch(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  materials: CityMaterialLibrary,
  maximumSegmentLengthM = Math.max(8, terrain.spacingM / 2),
): ReducedStreetSurfaceBatch | null {
  const meshSet = createStreetSurfaceMeshSet(
    features,
    terrain,
    family,
    {
      maximumSegmentLengthM,
      tileOrigin: tileOriginForId(tileId),
    },
  );
  const materialSet = materials.distantTransport[family];
  const object = new Group();
  object.name = `runtime-reduced-street-surfaces:${tileId}`;
  object.position.y = 0.02;
  object.userData = {
    tileId,
    sourceLayer: "transport-centerlines",
    styleFamily: family,
    representation:
      "reference-derived-coarse-junction-road-and-sidewalk-mesh",
    maximumSegmentLengthM,
    junctionCount: meshSet.junctionCount,
  };
  addMesh(
    object,
    mergeStreetMeshData([
      meshSet.carriageway,
      meshSet.shoulders,
      meshSet.elevatedDecks,
      meshSet.junctions,
    ]),
    materialSet.carriageway,
    `${object.name}:carriageway-and-junctions`,
    {
      featureIds: meshSet.carriagewayFeatureIds,
      junctionCount: meshSet.junctionCount,
      surface: "distant-carriageway-and-junction-context",
    },
    false,
  );
  addMesh(
    object,
    mergeStreetMeshData([
      meshSet.sidewalks,
      meshSet.pedestrian,
    ]),
    materialSet.pedestrian,
    `${object.name}:sidewalks-and-paths`,
    {
      featureIds: [
        ...meshSet.carriagewayFeatureIds,
        ...meshSet.pedestrianFeatureIds,
      ],
      surface: "distant-sidewalk-and-path-context",
    },
    false,
  );
  if (object.children.length === 0) {
    return null;
  }
  return {
    object,
    carriagewayCount: meshSet.carriagewayFeatureIds.length,
    pedestrianCount: meshSet.pedestrianFeatureIds.length,
    junctionCount: meshSet.junctionCount,
  };
}

function tileOriginForId(tileId: WorldTileId): Readonly<{ x: number; z: number }> {
  const address = parseWorldTileId(tileId);
  return {
    x: address.x * WORLD_TILE_SIZE_M,
    z: address.z * WORLD_TILE_SIZE_M,
  };
}

function addMesh(
  object: Group,
  data: Readonly<StreetMeshData> | null,
  material: MeshStandardMaterial,
  name: string,
  userData: Readonly<Record<string, unknown>>,
  receiveShadow = true,
): void {
  if (data === null) {
    return;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(data.positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(data.uvs, 2));
  geometry.setIndex([...data.indices]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = name;
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.receiveShadow = receiveShadow;
  mesh.userData = { ...object.userData, ...userData };
  object.add(mesh);
}

function mergeStreetMeshData(
  source: readonly (Readonly<StreetMeshData> | null)[],
): StreetMeshData | null {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (const data of source) {
    if (data === null) {
      continue;
    }
    const vertexOffset = positions.length / 3;
    for (const value of data.positions) {
      positions.push(value);
    }
    for (const value of data.uvs) {
      uvs.push(value);
    }
    for (const index of data.indices) {
      indices.push(index + vertexOffset);
    }
  }
  return positions.length === 0 ? null : { positions, uvs, indices };
}
